import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import config from "@/config";
import { CustomAxiosHttpRequest } from "@/utils/CustomAxiosHttpRequest";
import { OctraceClient } from "@advanced-sales-technologies/octrace-client";
import type { OpenAPIConfig } from "@advanced-sales-technologies/octrace-client/core/OpenAPI";
import { useLogto } from "@logto/react";
import { jwtDecode } from "jwt-decode";

export type ApiContextType = {
  apiClient: OctraceClient;
  accessToken: string | null;
  refreshToken: () => Promise<string>;
  waitForAuth: () => Promise<void>;
  isAuthReady: boolean;
};

// eslint-disable-next-line react-refresh/only-export-components
export const ApiContext = createContext<ApiContextType | undefined>(undefined);

const MAX_ATTEMPTS = 30;

const ApiProvider = ({ children }: { children: ReactNode }) => {
  const { getAccessToken, signIn, signOut, isLoading, isAuthenticated } = useLogto();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const isLoadingRef = useRef(isLoading);
  const isAuthenticatedRef = useRef(isAuthenticated);

  const isAuthReady = useMemo(() => !isLoading && isAuthenticated, [isLoading, isAuthenticated]);

  const waitForAuth = useCallback(async (): Promise<void> => {
    let attempts = 0;
    while (isLoadingRef.current && !isAuthenticatedRef.current) {
      if (attempts >= MAX_ATTEMPTS)
        throw new Error("Authentication timeout - failed to authenticate after 30 attempts");

      attempts++;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<string> => {
    const newToken = await getAccessToken(config.LOGTO_RESOURCE);
    if (!newToken) throw new Error("Failed to get access token");
    setAccessToken(newToken);
    return newToken;
  }, [getAccessToken]);

  const apiClient = useMemo(() => {
    const clientConfig = {
      BASE: config.BACKEND_URL,
      TOKEN: async () => {
        await waitForAuth();

        if (!accessToken || jwtDecode(accessToken).exp! < Date.now() / 1000) {
          const token = (await getAccessToken(config.LOGTO_RESOURCE)) as string | null;
          if (!token) await signIn(config.FRONTEND_URL + config.LOGTO_CALLBACK);
          setAccessToken(token);
          return token;
        }

        return accessToken;
      },
    } as Partial<OpenAPIConfig>;

    const createCustomHttpRequest = (refreshTokenFn: () => Promise<string>, signOutFn: () => void) => {
      return class extends CustomAxiosHttpRequest {
        constructor(config: OpenAPIConfig) {
          super(config, refreshTokenFn, signOutFn);
        }
      };
    };

    return new OctraceClient(
      clientConfig,
      createCustomHttpRequest(refreshToken, () => signOut(config.LOGTO_LOGOUT_URL)),
    );
  }, [accessToken, getAccessToken, signIn, refreshToken, signOut, waitForAuth]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
    isAuthenticatedRef.current = isAuthenticated;
  }, [isLoading, isAuthenticated]);

  return (
    <ApiContext.Provider value={{ apiClient, accessToken, refreshToken, waitForAuth, isAuthReady }}>
      {children}
    </ApiContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOctraceClient = (): ApiContextType => {
  const instance = useContext(ApiContext);
  if (!instance) {
    throw new Error("useOctraceClient must be used within OctraceClientProvider");
  }
  return instance;
};

export default ApiProvider;
